import { computed, signal, effect } from '@preact/signals-react';
import produce from 'immer';
import { useContext } from 'react';
import { createSignalsContext } from '@/signals/signal-context';
import {
  EditorChangeKind,
  EditorDeclarativeBlock,
} from '@/webapi/use-experience-api';
import { AiOriginAction, CompoundChange } from '@/pkg/sdk';
import {
  SelectedAgent,
  ContextualAttachment,
} from '@/features/ai-agents/shared/models';
import { hash } from '@/utils/cache';
import { EditorContext } from '@/features/editor/context/editor-context';
import { applySectionCaptions } from '@/features/ai-agents/chat/editor-config';
import { useCssManipulator } from '@/features/editor/widgets/shared/ai-editing-layout/manipulators/use-css-manipulator';
import {
  EDITOR_ACTIVE_BLOCK_CACHE,
  hashObject,
  useSignalCachedAutoSave,
} from '@/components/hooks/use-cached-auto-save';
import { useJavascriptManipulator } from '@/features/editor/widgets/shared/ai-editing-layout/manipulators/use-js-manipulator';
import { useHtmlManipulator } from '@/features/editor/widgets/shared/ai-editing-layout/manipulators/use-html-manipulator';
import { useCompoundChangeRenderer } from '@/features/editor/widgets/shared/ai-editing-layout/use-compound-change-renderer';
import { useVisualManifestBuilder } from '@/features/editor/widgets/shared/ai-editing-layout/manipulators/use-visual-manifest-builder';
import {
  AiComponentActions,
  AiComponentContextProps,
  AiComponentState,
  AiComponentTab,
  VisualGroupInfo,
  CodeBlockMutationOptions,
} from '@/features/editor/widgets/shared/ai-editing-layout/types';
import { useCodeMutations } from '@/features/editor/widgets/shared/ai-editing-layout/hooks/use-code-mutations';
import { useSessionManager } from '@/features/editor/widgets/shared/ai-editing-layout/hooks/use-session-manager';
import { useComponentUIState } from '@/features/editor/widgets/shared/ai-editing-layout/hooks/use-component-ui-state';
import { useChangePersistence } from '@/features/editor/widgets/shared/ai-editing-layout/hooks/use-change-persistence';
import { useComponentLifecycle } from '@/features/editor/widgets/shared/ai-editing-layout/hooks/use-component-lifecycle';

export type {
  VisualGroupInfo,
  AiComponentContextProps,
  AiComponentState,
  AiComponentActions,
  CodeBlockMutationOptions,
};

function createState(props?: AiComponentContextProps): AiComponentState {
  const newChange = signal(
    produce(props.originalChange, (draft) => {
      draft.editorKind = EditorChangeKind.NEW_COMPONENT;
      const value = draft.block.value as CompoundChange;
      value.isAiComponent = true;
      value.selectedAgent = props?.selectedAgent || {
        id: `widget-developer`,
        type: `agent`,
      };
      value.aiOrigin = props?.originAction || AiOriginAction.WIDGET_BUILDER;
    }),
  );
  const lastSavedChange = signal(props.originalChange);

  const selectedAgent = signal<SelectedAgent>(
    props?.selectedAgent || { id: `widget-developer`, type: `agent` },
  );

  const hasVisualEditManifest = computed(() => {
    const block = newChange.value.block.value as CompoundChange;
    return !!block.editManifest;
  });

  const selectedTab = signal(AiComponentTab.PROMPT);
  const selectedCodeTab = signal<`HTML` | `CSS` | `JS`>(`HTML`);
  const sessionId = computed(
    () => (newChange.value.block.value as CompoundChange).aiSessionId,
  );
  const firstSessionId = computed(() => {
    const value = newChange.value.block.value as CompoundChange;
    return value.aiFirstSessionId || value.aiSessionId;
  });

  const lastRunId = computed(() => {
    const value = newChange.value.block.value as CompoundChange;
    return value.lastRunId;
  });

  const isProcessing = signal(false);

  const isFullScreen = signal(false);

  const initialHash = hashChange(props?.originalChange);

  const hasChanges = computed(
    () => initialHash !== hashChange(newChange.value),
  );

  const isProcessingVisualManifest = signal(false);

  const sessionData = signal<Record<string, any>>({});

  const highlightedVisualGroup = signal<VisualGroupInfo | undefined>(undefined);

  const backToTab = signal<AiComponentTab | null>(AiComponentTab.PROMPT);

  const contextualAttachments = signal<ContextualAttachment[]>(
    props?.initialContextualAttachments || [],
  );

  const editorConfig = computed(() => {
    const block = newChange.value.block.value as CompoundChange;
    return block.editorConfig || null;
  });

  effect(() => {
    const config = editorConfig.value;
    if (config?.sections && Object.keys(config.sections)?.length > 0) {
      const currentAttachments = contextualAttachments.value;
      const updatedAttachments = applySectionCaptions(
        currentAttachments,
        config,
      );

      if (
        JSON.stringify(currentAttachments) !==
        JSON.stringify(updatedAttachments)
      ) {
        contextualAttachments.value = updatedAttachments;
      }
    }
  });

  const initialMessage = signal(props?.initialMessage);

  const state: AiComponentState = {
    isFullScreen,
    selectedTab,
    isProcessing,
    newChange,
    selectedCodeTab,
    sessionId,
    firstSessionId,
    lastRunId,
    isComponentReady: null as any, // Will be set below
    hasChanges,
    hasVisualEditManifest,
    isProcessingVisualManifest,
    sessionData,
    lastSavedChange,
    highlightedVisualGroup,
    backToTab,
    selectedAgent,
    contextualAttachments,
    editorConfig,
    starters: props?.starters,
    placeholder: props?.placeholder,
    initialMessage,
  };

  const defaultIsComponentReady = () => {
    if (isProcessing.value) return false;
    const block = newChange.value.block.value as CompoundChange;
    const isPlaceholder =
      block?.html?.includes(`__loomi_element_placeholder`) ||
      block?.html?.includes(`__visually_element_placeholder`);
    return !isPlaceholder && block.html?.length > 5 && !!block.aiSessionId;
  };

  // Apply override if provided, otherwise use default
  state.isComponentReady = computed(() =>
    props?.isComponentReadyOverride
      ? props.isComponentReadyOverride(state)
      : defaultIsComponentReady(),
  );

  return state;
}

function createActions(
  state: AiComponentState,
  props?: AiComponentContextProps,
): AiComponentActions {
  const {
    experienceState: { currentExperience },
  } = useContext(EditorContext);

  useSignalCachedAutoSave(
    state.newChange,
    currentExperience.id,
    EDITOR_ACTIVE_BLOCK_CACHE,
  );

  const { setHtml, setCss, setJs, setEditManifest } = useCodeMutations(
    state.newChange,
  );

  const { rerender, invalidate } = useCompoundChangeRenderer(
    state.newChange,
    state.isProcessing,
  );

  const { setCssRule, getCssRule, parsedCss } = useCssManipulator(
    state.newChange,
    setCss,
  );

  const { setVariable, getManifestVariables, jsVariables, parsedJs } =
    useJavascriptManipulator(state.newChange, setJs);

  const {
    getTextContent,
    setTextContent,
    getHtmlContent,
    setHtmlContent,
    getAttribute,
    setAttribute,
    getVideoAttributes,
    setVideoAttributes,
    removeByGroupName,
    removeAllByGroupName,
    parsedHtml,
  } = useHtmlManipulator(state.newChange, setHtml);

  const { isFirstInGroup } = useVisualManifestBuilder(
    state.newChange,
    parsedHtml,
    parsedCss,
    parsedJs,
    jsVariables,
  );

  const {
    onNewSessionId,
    onNewMessageSent,
    onPromptReply,
    onSessionLoaded,
    restoreCheckpoint,
  } = useSessionManager(state.newChange, state.selectedAgent, rerender, {
    onCodeBlockMutation: props?.onCodeBlockMutation,
  });

  const {
    selectTab,
    onSelectedCodeTab,
    toggleFullScreen,
    onLoadingChange,
    setBackToTab,
  } = useComponentUIState(
    state.selectedTab,
    state.selectedCodeTab,
    state.isFullScreen,
    state.isProcessing,
    state.backToTab,
  );

  const {
    applyChanges,
    discardChanges,
    saveLocalChanges,
    discardLocalChanges,
  } = useChangePersistence(state.newChange, state.lastSavedChange, selectTab);

  useComponentLifecycle(
    state.newChange,
    state.selectedTab,
    state.isFullScreen,
    state.highlightedVisualGroup,
    state.contextualAttachments,
    state.sessionData,
    {
      removeByGroupName,
      removeAllByGroupName,
      selectTab,
    },
  );

  const setContextualAttachments = (attachments: ContextualAttachment[]) => {
    state.contextualAttachments.value = attachments;
  };

  const getContextualAttachments = (): ContextualAttachment[] =>
    state.contextualAttachments.value;

  return {
    selectTab,
    onSelectedCodeTab,
    onNewSessionId,
    onNewMessageSent,
    onPromptReply,
    onSessionLoaded,
    setHtml,
    setCss,
    setJs,
    toggleFullScreen,
    discardChanges,
    applyChanges,
    getCssRule,
    setCssRule,
    setEditManifest,
    setVariable,
    getManifestVariables,
    getTextContent,
    setTextContent,
    getHtmlContent,
    setHtmlContent,
    getAttribute,
    setAttribute,
    getVideoAttributes,
    setVideoAttributes,
    onLoadingChange,
    saveLocalChanges,
    discardLocalChanges,
    isFirstInGroup,
    setBackToTab,
    restoreCheckpoint,
    setContextualAttachments,
    getContextualAttachments,
    rerender,
    invalidateRender: invalidate,
  };
}

export const [AiComponentContextProvider, useAiComponentContext] =
  createSignalsContext(createState, createActions);

function hashChange(originalChange: EditorDeclarativeBlock | undefined) {
  if (!originalChange) return ``;
  const block = originalChange.block.value as CompoundChange;
  return hash(
    `${block.html || ``}${block.css || ``}${block.js || ``}${
      block.aiSessionId || ``
    }${hashObject(block.editManifest || {})}`,
  );
}
